/**
 * Live per-connector power chart — FEAT-003 + BUG-005.
 *
 * Samples `effectivePowerW` from `useStationStore` once a second per
 * connector and renders a polyline over a 60-second WALL-CLOCK window
 * (BUG-005 fix: prior implementation used sample-count, so a throttled
 * background tab made the chart appear to compress / freeze).
 *
 * Idle tick keeps firing even when no transaction is active, so the X axis
 * advances continuously. On WS close + tx stop we push an explicit zero so
 * the trailing edge drops cleanly to 0 instead of staying pinned at the
 * last sample.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConnectionState } from '@/state/types';
import { useStationStore } from '@/state/useStationStore';
import { useInterval } from '@/hooks/useInterval';
import {
  pushZeroSamples,
  sampleConnectors,
  type Sample,
} from './powerChartBuffer';

const WINDOW_MS = 60_000;
const TICK_MS = 1_000;

export function PowerChart(): JSX.Element {
  const connectors = useStationStore((s) => s.connectors);
  const cpMaxPowerW = useStationStore((s) => s.cpMaxPowerW);
  const connectionState = useStationStore((s) => s.connectionState);

  const bufferRef = useRef<ReadonlyMap<number, readonly Sample[]>>(new Map());
  // BUG-006 — `tickValue` is read into `seriesEntries` useMemo deps so
  // each 1 s tick (or zero-push on WS close / tx stop) invalidates the
  // memo and re-reads `bufferRef.current`. Previously the discarded
  // tick state meant useMemo only refreshed when the connectors map
  // identity changed — which for an unbound connector NEVER happens
  // after startTransaction, leaving the legend pinned at the initial
  // 0 sample even while the buffer was actually filling at 22 kW.
  const [tickValue, setTick] = useState(0);
  const [paused, setPaused] = useState(() =>
    typeof document !== 'undefined' ? document.hidden : false,
  );

  // Track the previous values of (connectionState, activeTxIds) so we can
  // notice transitions and synthesize a zero-sample on the drop edge.
  const prevConnRef = useRef<ConnectionState>(connectionState);
  const prevActiveIdsRef = useRef<readonly number[]>([]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handler = (): void => setPaused(document.hidden);
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // BUG-005 — push 0 on WS close + tx stop transitions so the polyline
  // visibly drops instead of holding at the last positive value.
  useEffect(() => {
    const now = Date.now();
    let mutated = false;
    if (
      prevConnRef.current === ConnectionState.Connected &&
      connectionState !== ConnectionState.Connected
    ) {
      bufferRef.current = pushZeroSamples(bufferRef.current, WINDOW_MS, now);
      mutated = true;
    }
    const liveActiveIds = [...connectors.values()]
      .filter((c) => c.activeTransactionId !== null)
      .map((c) => Number(c.id));
    const droppedTx = prevActiveIdsRef.current.some((id) => !liveActiveIds.includes(id));
    if (droppedTx) {
      bufferRef.current = pushZeroSamples(bufferRef.current, WINDOW_MS, now);
      mutated = true;
    }
    prevConnRef.current = connectionState;
    prevActiveIdsRef.current = liveActiveIds;
    if (mutated) setTick((t) => t + 1);
  }, [connectionState, connectors]);

  const tick = useCallback(() => {
    const next = sampleConnectors(bufferRef.current, connectors, WINDOW_MS, Date.now());
    bufferRef.current = next;
    setTick((t) => t + 1);
  }, [connectors]);

  useInterval(tick, paused ? null : TICK_MS);

  const seriesEntries = useMemo(() => {
    const out: { id: number; samples: readonly Sample[] }[] = [];
    for (const c of connectors.values()) {
      out.push({ id: Number(c.id), samples: bufferRef.current.get(Number(c.id)) ?? [] });
    }
    return out;
    // BUG-006 — `tickValue` MUST stay in deps. ESLint flags it as
    // "unnecessary" because it isn't directly read inside the memo,
    // but it's used as an invalidation signal: each `tick` callback
    // mutates `bufferRef.current` (a ref, untracked by the deps
    // linter) and bumps `tickValue`, which forces this memo to
    // re-read the fresh buffer. Without this dep, ticks that don't
    // also change the `connectors` map identity leave the memo
    // pinned to the initial empty samples arrays and the chart
    // shows 0 forever (see plan.md#Session-8f.12 BUG-006 rationale).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectors, tickValue]);

  // SVG viewport — 320×120 internal coordinates, scales via width=100%.
  const W = 320;
  const H = 120;
  const PAD = 4;
  const yMax = Math.max(cpMaxPowerW, 1);
  const now = Date.now();

  return (
    <section
      className="rounded border border-slate-700 bg-slate-900/60 p-4"
      aria-label="Power chart"
      data-testid="power-chart"
    >
      <header className="mb-3 flex items-baseline justify-between text-sm">
        <h2 className="font-medium text-slate-100">Power chart</h2>
        <span className="text-xs text-slate-400">
          {paused ? 'paused (tab hidden)' : `last ${WINDOW_MS / 1000}s`}
        </span>
      </header>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-hidden="true">
        {/* baseline + max gridlines */}
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} className="stroke-slate-700" />
        <line x1={PAD} y1={PAD} x2={W - PAD} y2={PAD} className="stroke-slate-800" strokeDasharray="2 3" />
        {seriesEntries.map((s, idx) => (
          <ConnectorSeries
            key={s.id}
            id={s.id}
            samples={s.samples}
            now={now}
            windowMs={WINDOW_MS}
            yMax={yMax}
            colorIdx={idx}
            w={W}
            h={H}
            pad={PAD}
          />
        ))}
      </svg>
      <ul className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-300">
        {seriesEntries.map((s, idx) => (
          <li key={s.id} className="flex items-center gap-1">
            <span className={`inline-block h-2 w-3 ${COLORS[idx % COLORS.length]}`} />
            Connector {s.id}: {((s.samples.at(-1)?.value ?? 0) / 1000).toFixed(2)} kW
          </li>
        ))}
      </ul>
    </section>
  );
}

const COLORS = [
  'bg-primary-base',
  'bg-sky-400',
  'bg-amber-400',
  'bg-rose-400',
  'bg-violet-400',
] as const;

const STROKE_COLORS = [
  'stroke-primary-base',
  'stroke-sky-400',
  'stroke-amber-400',
  'stroke-rose-400',
  'stroke-violet-400',
] as const;

interface SeriesProps {
  readonly id: number;
  readonly samples: readonly Sample[];
  readonly now: number;
  readonly windowMs: number;
  readonly yMax: number;
  readonly colorIdx: number;
  readonly w: number;
  readonly h: number;
  readonly pad: number;
}

function ConnectorSeries({
  id,
  samples,
  now,
  windowMs,
  yMax,
  colorIdx,
  w,
  h,
  pad,
}: SeriesProps): JSX.Element {
  if (samples.length < 2) return <></>;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;
  // Wall-clock projection: map (now - windowMs .. now) → (pad .. w - pad).
  const leftT = now - windowMs;
  const points = samples
    .map((s) => {
      const tFrac = Math.max(0, Math.min(1, (s.t - leftT) / windowMs));
      const x = pad + tFrac * innerW;
      const y = pad + innerH - Math.min(1, s.value / yMax) * innerH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <polyline
      points={points}
      fill="none"
      strokeWidth={1.5}
      className={STROKE_COLORS[colorIdx % STROKE_COLORS.length]}
      data-testid={`power-chart-series-${id}`}
    />
  );
}
